import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api";
import {
  apiTrafos,
  CONCLUIDO,
  dataBR,
  descricaoTrafo,
  duracao,
  entregaAtrasada,
  haQuanto,
  nomeEtapa,
  type TrafoResumo,
} from "../trafos";
import TrafoFicha from "./TrafoFicha";
import { Trilha } from "./TrafoEtapas";
import ErrorState from "./ErrorState";
import { IconPlus, IconRefresh, IconSearch } from "../icons";

/**
 * TRAFOS EM REFORMA — a tela do colaborador (app "Operacional").
 *
 * Lista os trafos da oficina com a etapa em que cada um está; "Nova entrada de
 * trafo" registra a chegada e a ficha (TrafoFicha) leva o trafo pelas 5 etapas.
 * Todo mundo vê todos os trafos: na oficina, quem recebe não é quem ensaia nem
 * quem faz a inspeção final — cada um pega o trafo na etapa em que ele está.
 *
 * A lista se atualiza sozinha a cada 20 s (com a tela aberta e visível): o que
 * o colega acabou de fazer aparece sem precisar recarregar.
 */

type Filtro = "andamento" | "concluidos";
const ATUALIZA_MS = 20_000;

export default function TrafosReformaView({ somenteLeitura = false }: { somenteLeitura?: boolean }) {
  const [filtro, setFiltro] = useState<Filtro>("andamento");
  const [busca, setBusca] = useState("");
  const [lista, setLista] = useState<TrafoResumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  /** undefined = fechada; null = nova entrada; número = ficha do trafo. */
  const [aberto, setAberto] = useState<number | null | undefined>(undefined);
  const [agora, setAgora] = useState(Date.now());

  const carregar = useCallback(
    async (silencioso = false) => {
      if (!silencioso) setCarregando(true);
      try {
        setLista(await apiTrafos.listar(filtro));
        setErro(null);
        setAgora(Date.now());
      } catch (err) {
        if (!silencioso) setErro(err instanceof ApiError ? err : new ApiError(String(err)));
      } finally {
        if (!silencioso) setCarregando(false);
      }
    },
    [filtro],
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") carregar(true);
    }, ATUALIZA_MS);
    return () => clearInterval(timer);
  }, [carregar]);

  const visiveis = useMemo(() => {
    const q = busca
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .trim()
      .toLowerCase();
    if (!q) return lista;
    return lista.filter((t) =>
      [t.id, t.cliente, t.os_numero, t.nota_fiscal, t.identificacao, t.fabricante, t.numero_serie, t.potencia_kva]
        .map((v) => String(v ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase())
        .some((v) => v.includes(q)),
    );
  }, [lista, busca]);

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={() => carregar()} />;

  return (
    <div className="trafos">
      <div className="crud-toolbar trafos-barra">
        <div className="trafos-filtros" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={filtro === "andamento"}
            className={`trafos-filtro${filtro === "andamento" ? " on" : ""}`}
            onClick={() => setFiltro("andamento")}
          >
            Em andamento
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filtro === "concluidos"}
            className={`trafos-filtro${filtro === "concluidos" ? " on" : ""}`}
            onClick={() => setFiltro("concluidos")}
          >
            Concluídos
          </button>
        </div>
        <div className="toolbar-right trafos-barra-direita">
          <div className="search-wrap">
            <IconSearch size={16} />
            <input
              className="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Cliente, OS, série, nº do trafo…"
            />
          </div>
          <button type="button" className="icon-btn" onClick={() => carregar()} title="Atualizar agora" aria-label="Atualizar">
            <IconRefresh size={18} />
          </button>
          {!somenteLeitura && (
            <button type="button" className="btn-primary" onClick={() => setAberto(null)}>
              <IconPlus size={16} /> Nova entrada de trafo
            </button>
          )}
        </div>
      </div>

      {carregando && lista.length === 0 ? (
        <div className="empty">Carregando…</div>
      ) : visiveis.length === 0 ? (
        <div className="empty">
          {busca
            ? "Nenhum trafo encontrado com essa busca."
            : filtro === "andamento"
              ? "Nenhum trafo em reforma agora. Quando um trafo chegar, toque em “Nova entrada de trafo”."
              : "Nenhum trafo concluído ainda."}
        </div>
      ) : (
        <div className="trafos-grade">
          {visiveis.map((t) => (
            <button key={t.id} type="button" className="trafo-cartao" onClick={() => setAberto(t.id)}>
              <div className="trafo-cartao-topo">
                <span className="trafo-cartao-num">Nº {t.id}</span>
                <strong className="trafo-cartao-cliente">{t.cliente}</strong>
              </div>
              <span className="trafo-cartao-desc">
                {descricaoTrafo(t)}
                {t.os_numero ? ` · OS ${t.os_numero}` : ""}
              </span>
              <Trilha etapa={t.etapa} />
              <div className="trafo-cartao-etapa">
                {t.etapa === CONCLUIDO ? (
                  <span>
                    Concluído {t.data_conclusao ? haQuanto(t.data_conclusao, agora) : ""}
                  </span>
                ) : (
                  <span>
                    <strong>{nomeEtapa(t.etapa)}</strong> · há {duracao(t.etapa_desde, agora)}
                  </span>
                )}
                {t.servicos_total > 0 && (
                  <span className="muted">
                    Serviços {t.servicos_feitos}/{t.servicos_total}
                  </span>
                )}
              </div>
              {entregaAtrasada(t) && (
                <span className="trafo-atrasado">Entrega prevista {dataBR(t.previsao_entrega)} — vencida</span>
              )}
              {t.ultimo_texto && (
                <span className="trafo-cartao-ultimo">
                  {t.ultimo_texto}
                  <span className="muted">
                    {" "}
                    — {t.ultimo_usuario ?? "—"}, {haQuanto(t.ultimo_em, agora)}
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {aberto !== undefined && (
        <TrafoFicha
          id={aberto}
          somenteLeitura={somenteLeitura}
          onClose={() => setAberto(undefined)}
          onChanged={() => carregar(true)}
        />
      )}
    </div>
  );
}
